import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../contexts/StoreContext';
import GestureArea from '../../gesture-area/GestureArea';
import {
  GestureStateChangeEvent,
  GestureUpdateEvent,
  PanGestureHandlerEventPayload,
  PinchGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import { UserImageWithTransform as UserImage } from '../../user-image/UserImageWithTransform';
import { IUserImageAreaModel, UserImageAreaModelInstance } from '../../../store/_models/userImageArea';
import { IUserImageModel, UserImageModelInstance } from '../../../store/_models/userImage';
import { calculateZoomValue } from '../../../helpers/zoom.helper';
import { UserImageArea } from '@photosi/rn-components/user-image-area/UserImageArea';
import { UserImageAreaGridOverlay } from '@photosi/rn-components/user-image-area/user-image-area-grid-overlay/UserImageAreaGridOverlay';
import { useWheelZoom } from '../../../hooks/useWheelZoom';
import { BadgeQualityAlert } from '@photosi/rn-components/badge-quality-alert/BadgeQualityAlert';
import { PreviewModelInstance } from '../../../store/_models/preview';
import { emit } from '../../../contexts/TransformEventBus';
import { BadgeCropAlert } from '@photosi/rn-components/badge-crop-alert/BadgeCropAlert';
import { withActiveUserImage } from 'src/hoc/withActiveUserImage';
import { withActiveUserImageArea } from 'src/hoc/withActiveUserImageArea';
import { neutrals } from 'src/theme/colors';
import { useRoute } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { View } from 'react-native';

export type UserImagesFullscreenProps = {
  activeUserImageArea: UserImageAreaModelInstance;
  activeUserImage: UserImageModelInstance;
  preview: PreviewModelInstance;
  editable: boolean;
  showGrid: boolean;
  disabled?: boolean;
  disableSwapImageAnimation?: boolean;
};

/**
 * Represents the collage of all the images to be inserted into the
 * photo space (userImageAreas) in the product (userImagesForPreviewSvg).
 */
const FullscreenPreviewContentComponent = observer(
  ({
    activeUserImageArea: userImageArea,
    activeUserImage: userImage,
    preview,
    editable = false,
    showGrid = false,
    disabled = false,
    disableSwapImageAnimation = false,
  }: UserImagesFullscreenProps) => {
    // console.log("FullscreenPreviewContent rerender");
    const {
      project: { selectUserImage, activeProductId },
    } = useStore();

    const route = useRoute();
    const newRoute = [ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES);
    const lastPinchZoomRef = useRef<number | undefined>();
    const [hoveringAreaId, setHoveringAreaId] = useState<string | null>(null);

    const isActiveProduct = preview.productId === activeProductId;

    // Zoom with wheel logic
    const isHoveringOnActiveUserImageArea = !!hoveringAreaId && hoveringAreaId === userImageArea?.id;
    const isZoomWithWheelActive = isHoveringOnActiveUserImageArea && !preview.isAnyUserImageUpdating;
    useWheelZoom({ isActive: isZoomWithWheelActive, userImage: userImageArea?.userImage });

    /**
     * GESTURE HANDLERS
     */

    // Calculate the new image scale using a dynamic speed approach
    const calculateScaleFrom = (userImage: IUserImageModel, eventScale: number) => {
      if (lastPinchZoomRef.current === undefined) lastPinchZoomRef.current = eventScale;
      const zoomFactor = eventScale / lastPinchZoomRef.current;
      lastPinchZoomRef.current = eventScale;
      return calculateZoomValue(userImage.scale * zoomFactor, userImage.minScale, userImage.maxScale);
    };

    const pinchHandler = (event: GestureUpdateEvent<PinchGestureHandlerEventPayload>, userImage: IUserImageModel) => {
      const newScale = calculateScaleFrom(userImage, event.scale);

      // userImage.setScale(newScale, responsiveScale);
      emit({
        type: 'temp-zoom',
        userImageId: userImage.id,
        payload: { absoluteScale: newScale },
        source: 'gesture',
      });
    };

    const pinchEndHandler = (
      event: GestureStateChangeEvent<PinchGestureHandlerEventPayload>,
      userImage: IUserImageModel,
    ) => {
      const newScale = calculateScaleFrom(userImage, event.scale);

      // userImage.applyScale(newScale, responsiveScale);
      emit({
        type: 'apply-zoom',
        userImageId: userImage.id,
        payload: { absoluteScale: newScale },
        source: 'gesture',
      });

      lastPinchZoomRef.current = undefined;
    };

    /**
     * SELECT IMAGE HANDLERS
     */
    const tapHandler = ({ userImage }: IUserImageAreaModel) => {
      selectUserImage(userImage);
    };

    /**
     * DRAG IMAGE HANDLERS
     */
    const dragImageChange = (event: GestureUpdateEvent<PanGestureHandlerEventPayload>, userImage: IUserImageModel) => {
      // userImage.setOffsets({ x: event.translationX, y: event.translationY });

      // NUOVO: Emit evento temporaneo direttamente da gesture (UI thread)
      emit({
        type: 'temp-pan',
        userImageId: userImage.id,
        payload: { x: event.translationX, y: event.translationY },
        source: 'gesture',
      });
    };

    const dragImageEnd = (
      event: GestureStateChangeEvent<PanGestureHandlerEventPayload>,
      userImage: IUserImageModel,
    ) => {
      // userImage.applyOffsets({ x: event.translationX, y: event.translationY }, responsiveScale);

      // NUOVO: Emit evento apply direttamente da gesture (UI thread)
      emit({
        type: 'apply-pan',
        userImageId: userImage.id,
        payload: { x: event.translationX, y: event.translationY },
        source: 'gesture',
      });
    };

    const isSelected: boolean = true;
    const showGridOverlay: boolean = isSelected && editable && showGrid;
    const isQualityBadgeVisible: boolean = !!userImage?.qualityAlert && !!userImage?.qualitySuggestions?.options.length;
    const isCropBadgeVisible: boolean = userImage.cropAlert && !editable;

    return (
      <UserImageArea userImageArea={userImageArea} isSelected={isSelected} ignoreOffsets>
        <GestureArea
          disabled={disabled || !isActiveProduct || preview.isAnyUserImageUpdating}
          onPan={event => dragImageChange(event, userImage)}
          onPanEnd={event => dragImageEnd(event, userImage)}
          onPinch={event => pinchHandler(event, userImage)}
          onPinchEnd={event => pinchEndHandler(event, userImage)}
          onTap={() => tapHandler(userImageArea)}
          onHoverStart={() => setHoveringAreaId(userImageArea.id)}
          onHoverEnd={() => setHoveringAreaId(prev => (prev === userImageArea.id ? null : prev))}
        >
          {showGridOverlay && <UserImageAreaGridOverlay extBorderColor={newRoute ? '#FAA100' : neutrals.white} />}
          <UserImage
            editable={false}
            userImage={userImage}
            disableSwapImageAnimation={disableSwapImageAnimation}
            showOverflow
          />
          <UserImage editable={editable} userImage={userImage} disableSwapImageAnimation={disableSwapImageAnimation} />
        </GestureArea>
        {isQualityBadgeVisible && (
          <View style={{ position: 'absolute', zIndex: 200, bottom: 10, right: 10 }}>
            <BadgeQualityAlert userImage={userImage} />
          </View>
        )}
        {isCropBadgeVisible && <BadgeCropAlert userImage={userImage} />}
      </UserImageArea>
    );
  },
);

export const FullscreenPreviewContent = withActiveUserImageArea(
  withActiveUserImage<UserImagesFullscreenProps>(FullscreenPreviewContentComponent),
);
